/**
 * CommandPalette.jsx — Canvas V2 Cmd+K palette (#171 / Tier-2).
 *
 * Opens over the canvas on ⌘K / Ctrl+K. Fuzzy-searches every rendered
 * node and edge case-label, jumps the ReactFlow viewport to the selection
 * with a brief focus pulse, and closes on Esc or outside click.
 *
 * Conductor OSS doesn't offer this. In a demo it's a "we out-craft them on the
 * tiny stuff" moment — one keypress, full graph traversal — that
 * compounds with every other navigation affordance.
 *
 * Architecture
 * ────────────
 * - The palette is a sibling of <ReactFlow>, mounted inside the same
 *   provider so `useReactFlow` has access to viewport APIs.
 * - Parent (CanvasPageV2) owns `open`/`onClose` and passes the current
 *   rfNodes/rfEdges so the index rebuilds when the canvas does.
 * - A client-side fuzzy matcher scores items on substring hits + prefix
 *   bonus + type-keyword bonus. No dependencies.
 * - Viewport jump uses `setCenter(x, y, { zoom, duration })` so the
 *   movement is animated, not a teleport — the context of "where we
 *   came from" is preserved in the viewer's head.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useReactFlow } from '@xyflow/react';
import { V2_PAL, V2_STONE, V2_BRAND } from './nodes/v2Palette';

// Ranked label for each v2 kind — drives both the icon chip color and
// the "search by type" shortcut (typing `switch` ranks switch containers
// above everything else).
const KIND_META = Object.freeze({
  start:                  { group: 'Sentinel',  typeWord: 'start',      color: V2_PAL.start.bg },
  end:                    { group: 'Sentinel',  typeWord: 'end',        color: V2_PAL.end.bg },
  leaf:                   { group: 'Task',      typeWord: 'task',       color: V2_STONE[500] },
  switchHead:             { group: 'Switch',    typeWord: 'switch',     color: V2_PAL.switch.head.border },
  switchContainer:        { group: 'Switch',    typeWord: 'switch',     color: V2_PAL.switch.head.border },
  caseContainer:          { group: 'Case',      typeWord: 'case',       color: V2_PAL.switch.head.border },
  forkHead:               { group: 'Fork',      typeWord: 'fork',       color: V2_PAL.fork.head.border },
  forkContainer:          { group: 'Fork',      typeWord: 'fork',       color: V2_PAL.fork.head.border },
  legContainer:           { group: 'Leg',       typeWord: 'leg',        color: V2_PAL.fork.head.border },
  dynForkHead:            { group: 'DynFork',   typeWord: 'dyn-fork',   color: V2_PAL.dynFork.head.border },
  dynForkContainer:       { group: 'DynFork',   typeWord: 'dyn-fork',   color: V2_PAL.dynFork.head.border },
  dynLegTemplate:         { group: 'DynFork',   typeWord: 'dyn-fork',   color: V2_PAL.dynFork.head.border },
  join:                   { group: 'Join',      typeWord: 'join',       color: V2_PAL.join.staticBorder },
  loopHead:               { group: 'Loop',      typeWord: 'loop',       color: V2_PAL.loop.head.border },
  loopContainer:          { group: 'Loop',      typeWord: 'loop',       color: V2_PAL.loop.head.border },
  loopBody:               { group: 'Loop',      typeWord: 'loop',       color: V2_PAL.loop.head.border },
  subWorkflow:            { group: 'SubWorkflow', typeWord: 'subworkflow', color: V2_PAL.subWorkflow.sync.border },
  subWorkflowHead:        { group: 'SubWorkflow', typeWord: 'subworkflow', color: V2_PAL.subWorkflow.sync.border },
  subWorkflowContainer:   { group: 'SubWorkflow', typeWord: 'subworkflow', color: V2_PAL.subWorkflow.sync.border },
  terminate:              { group: 'Terminate', typeWord: 'terminate',  color: V2_PAL.terminate.failed.border },
  meet:                   { group: 'Meet',      typeWord: 'meet',       color: V2_STONE[500] },
});

// ── Index building ──────────────────────────────────────────────────
// Turn rfNodes into a flat `items` array the matcher can scan. We keep
// the geometry (position + size) so Enter can jump-to immediately
// without another lookup.
function buildIndex(rfNodes) {
  if (!Array.isArray(rfNodes)) return [];
  const items = [];
  for (const n of rfNodes) {
    if (!n || !n.data) continue;
    const kind = n.data.kind || '';
    // Skip purely structural nodes the user never cares to navigate to
    // (tiny meet dots, empty synthesised containers).
    if (kind === 'meet') continue;
    const meta = KIND_META[kind] || { group: 'Node', typeWord: kind, color: V2_STONE[500] };
    const label = (n.data.label || n.data.name || n.id || '').toString();
    const ref = n.data.refName
      ? `${n.data.refName}${n.data.refVersion != null ? `@${n.data.refVersion}` : ''}`
      : '';
    items.push({
      id: n.id,
      sourceNodeId: n.data.sourceNodeId || null,
      kind,
      group: meta.group,
      typeWord: meta.typeWord,
      color: meta.color,
      label,
      ref,
      position: n.position || { x: 0, y: 0 },
      width: n.width || n.style?.width || 260,
      height: n.height || n.style?.height || 80,
      // Pre-compute a single searchable string for quick scoring.
      _search: `${label} ${meta.typeWord} ${meta.group} ${ref}`.toLowerCase(),
    });
  }
  return items;
}

// ── Fuzzy matching ──────────────────────────────────────────────────
// Simple multi-term scorer:
//   +5  per exact substring match of full query in label (prefix = +10)
//   +3  per term substring match
//   +2  bonus if term matches the typeWord (e.g. "switch" finds switches)
//   +1  per character in the query that appears in order anywhere
//
// Designed to feel instant on canvases of 100–500 nodes. Nothing fancy.
function scoreItem(item, rawQuery) {
  if (!rawQuery) return 1; // empty query — show everything
  const q = rawQuery.trim().toLowerCase();
  if (!q) return 1;
  const hay = item._search;
  let score = 0;
  // Full-query substring boost.
  const fullHit = hay.indexOf(q);
  if (fullHit === 0) score += 10;
  else if (fullHit > 0) score += 5;

  const terms = q.split(/\s+/).filter(Boolean);
  for (const t of terms) {
    if (!t) continue;
    if (hay.indexOf(t) >= 0) score += 3;
    if (item.typeWord === t || item.typeWord.indexOf(t) === 0) score += 2;
    // Character-in-order fallback.
    let ci = 0;
    for (let i = 0; i < hay.length && ci < t.length; i++) {
      if (hay[i] === t[ci]) ci++;
    }
    if (ci === t.length) score += 1;
  }
  return score;
}

function filterAndRank(items, query, limit = 40) {
  const scored = [];
  for (const it of items) {
    const s = scoreItem(it, query);
    if (s > 0) scored.push({ item: it, score: s });
  }
  scored.sort((a, b) => b.score - a.score || a.item.label.localeCompare(b.item.label));
  return scored.slice(0, limit).map((x) => x.item);
}

// ── Component ───────────────────────────────────────────────────────

export default function CommandPalette({ open, onClose, rfNodes, onActivate }) {
  const { setCenter } = useReactFlow();
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const items = useMemo(() => buildIndex(rfNodes), [rfNodes]);
  const results = useMemo(() => filterAndRank(items, query), [items, query]);

  // Reset on open — focus the input, clear the query, park selection at 0.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelectedIdx(0);
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 15);
    return () => clearTimeout(t);
  }, [open]);

  // Keep selection in range when results shrink/grow.
  useEffect(() => {
    if (selectedIdx >= results.length) setSelectedIdx(Math.max(0, results.length - 1));
  }, [results.length, selectedIdx]);

  // Scroll the selected row into view when arrow-navigating.
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.querySelector(`[data-cp-row="${selectedIdx}"]`);
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [selectedIdx]);

  const activate = useCallback((item) => {
    if (!item) return;
    const cx = item.position.x + item.width / 2;
    const cy = item.position.y + item.height / 2;
    setCenter(cx, cy, { zoom: 1.15, duration: 600 });
    if (typeof onActivate === 'function') onActivate(item);
    onClose();
  }, [setCenter, onClose, onActivate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      activate(results[selectedIdx]);
      return;
    }
  }, [onClose, results, selectedIdx, activate]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Canvas command palette"
      onClick={onClose}
      style={{
        position: 'absolute', inset: 0, zIndex: 50,
        background: 'rgba(15, 23, 42, 0.35)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: 84,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        style={{
          width: 560, maxWidth: 'calc(100% - 48px)',
          background: '#ffffff',
          border: `1px solid ${V2_STONE[300]}`,
          borderRadius: 12,
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px',
          borderBottom: `1px solid ${V2_STONE[200]}`,
        }}>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            color: V2_BRAND.dark, padding: '2px 8px',
            border: `1px solid ${V2_BRAND.muted}`, borderRadius: 999,
            background: V2_BRAND.soft,
          }}>⌘⇧F</span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Jump to any node — task name, switch, fork, terminate…"
            onChange={(e) => { setQuery(e.target.value); setSelectedIdx(0); }}
            spellCheck={false}
            autoComplete="off"
            style={{
              flex: 1,
              fontSize: 15, fontWeight: 500,
              border: 'none', outline: 'none',
              background: 'transparent', color: V2_STONE[900],
            }}
          />
          <span style={{ fontSize: 11, color: V2_STONE[500] }}>
            {results.length} {results.length === 1 ? 'match' : 'matches'}
          </span>
        </div>

        <div
          ref={listRef}
          style={{
            maxHeight: 360, overflowY: 'auto',
            padding: 4,
          }}
        >
          {results.length === 0 && (
            <div style={{
              padding: '28px 14px', fontSize: 13, color: V2_STONE[500],
              textAlign: 'center',
            }}>
              No matches. Try a task label, a node type (<code>switch</code>, <code>fork</code>, <code>loop</code>), or a SubWorkflow ref.
            </div>
          )}
          {results.map((item, idx) => {
            const selected = idx === selectedIdx;
            return (
              <div
                key={item.id}
                data-cp-row={idx}
                onMouseEnter={() => setSelectedIdx(idx)}
                onClick={() => activate(item)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: selected ? V2_BRAND.soft : 'transparent',
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: item.color, flexShrink: 0,
                }} />
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                  color: V2_STONE[500], textTransform: 'uppercase',
                  minWidth: 60,
                }}>{item.group}</span>
                <span style={{
                  flex: 1, minWidth: 0,
                  fontSize: 13, fontWeight: 500,
                  color: V2_STONE[900],
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{item.label}</span>
                {item.ref && (
                  <span style={{
                    fontSize: 10, color: V2_STONE[500],
                    fontFamily: 'SFMono-Regular, Menlo, monospace',
                    background: V2_STONE[100], padding: '1px 6px', borderRadius: 4,
                    whiteSpace: 'nowrap',
                  }}>{item.ref}</span>
                )}
                {selected && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, color: V2_BRAND.primary,
                    padding: '2px 6px', border: `1px solid ${V2_BRAND.muted}`,
                    borderRadius: 4, background: '#ffffff',
                  }}>↵</span>
                )}
              </div>
            );
          })}
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px',
          borderTop: `1px solid ${V2_STONE[200]}`,
          background: V2_STONE[50] || '#fafafa',
          fontSize: 11, color: V2_STONE[500],
        }}>
          <span>
            <kbd style={kbdStyle}>↑</kbd>
            <kbd style={kbdStyle}>↓</kbd>
            navigate · <kbd style={kbdStyle}>↵</kbd> jump · <kbd style={kbdStyle}>esc</kbd> close
          </span>
          <span style={{ fontWeight: 500, color: V2_BRAND.primary }}>Canvas V2</span>
        </div>
      </div>
    </div>
  );
}

const kbdStyle = {
  display: 'inline-block',
  margin: '0 2px',
  padding: '1px 5px',
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  fontSize: 10,
  color: V2_STONE[700],
  background: '#ffffff',
  border: `1px solid ${V2_STONE[300]}`,
  borderRadius: 3,
};
